import React from "react";
import { useTranslation } from "react-i18next";
import { ScanLine, FileText, ShieldAlert, Quote } from "lucide-react";

/**
 * Extraction quality badges shown next to the risk badge on the Analysis page.
 *
 * Displays:
 *  - Method:           "OCR utilisé" (amber) | "Texte natif" (zinc) | "Hybride" (blue)
 *  - OCR confidence:   "Confiance OCR: XX%" pill (color-tied to confidence band)
 *  - Verification:     "Vérification requise" rose pill when LLM/server flagged
 *
 * All elements are clickable-friendly (title tooltips) and carry data-testid.
 *
 * Props
 * -----
 * extractionCache: { method, ocr_confidence, pages_native[], pages_ocr[], page_count }
 * analysis:        { extraction_confidence: 'low'|'medium'|'high', requires_verification: bool }
 */
export function ExtractionBadges({ extractionCache, analysis }) {
  const { t } = useTranslation("analysis");
  const method = extractionCache?.method;
  const confidence = extractionCache?.ocr_confidence;
  const pagesOcr = extractionCache?.pages_ocr?.length || 0;
  const pageCount = extractionCache?.page_count || 0;
  const llmConfidence = analysis?.extraction_confidence;
  const requiresVerification = !!analysis?.requires_verification;

  if (!method && !llmConfidence) return null;

  const methodLabel =
    method === "ocr" ? t("badges.ocr_used", "OCR utilisé")
      : method === "hybrid" ? t("badges.hybrid", "OCR + Natif")
        : method === "failed" ? t("badges.extraction_failed", "Extraction échouée")
          : t("badges.native_text", "Texte natif");

  const methodColor =
    method === "ocr" ? "border-amber-200 bg-amber-50 text-amber-800"
      : method === "hybrid" ? "border-blue-200 bg-blue-50 text-blue-800"
        : method === "failed" ? "border-rose-200 bg-rose-50 text-rose-800"
          : "border-zinc-200 bg-zinc-50 text-zinc-700";

  const methodIcon =
    method === "ocr" || method === "hybrid"
      ? <ScanLine size={13} strokeWidth={1.75} />
      : <FileText size={13} strokeWidth={1.75} />;

  // Confidence band (0..1)
  const confPct = typeof confidence === "number" ? Math.round(confidence * 100) : null;
  const confColor =
    confPct == null ? "border-zinc-200 bg-zinc-50 text-zinc-600"
      : confPct >= 85 ? "border-emerald-200 bg-emerald-50 text-emerald-800"
        : confPct >= 70 ? "border-amber-200 bg-amber-50 text-amber-800"
          : "border-rose-200 bg-rose-50 text-rose-800";

  return (
    <div className="flex items-center flex-wrap gap-2" data-testid="extraction-badges">
      <span
        title={method === "ocr"
          ? `${pagesOcr}/${pageCount} pages OCR`
          : method === "hybrid"
            ? `${pagesOcr}/${pageCount} pages OCR`
            : t("badges.native_text_title", "Texte extrait nativement du PDF")}
        className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium ${methodColor}`}
        data-testid="extraction-method-badge"
      >
        {methodIcon}
        {methodLabel}
        {(method === "ocr" || method === "hybrid") && pageCount > 0 && (
          <span className="opacity-70">· {pagesOcr}/{pageCount}p</span>
        )}
      </span>

      {confPct != null && (
        <span
          title={t("badges.ocr_confidence_title", "Confiance OCR moyenne pondérée")}
          className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium ${confColor}`}
          data-testid="extraction-confidence-badge"
        >
          {t("badges.ocr_confidence", "Confiance OCR")}: {confPct}%
        </span>
      )}

      {requiresVerification && (
        <span
          title={t("badges.verification_required_title",
            "Une ou plusieurs dates critiques sont ambiguës — vérifier manuellement avant toute action.")}
          className="inline-flex items-center gap-1.5 rounded-full border border-rose-200 bg-rose-50 px-2.5 py-1 text-xs font-bold text-rose-800"
          data-testid="extraction-verification-badge"
        >
          <ShieldAlert size={13} strokeWidth={2} />
          {t("badges.verification_required", "Vérification requise")}
        </span>
      )}

      {llmConfidence && !requiresVerification && (
        <span
          title={t("badges.llm_confidence_title", "Confiance d'extraction des dates")}
          className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium ${
            llmConfidence === "high" ? "border-emerald-200 bg-emerald-50 text-emerald-800"
              : llmConfidence === "medium" ? "border-zinc-200 bg-zinc-50 text-zinc-700"
                : "border-rose-200 bg-rose-50 text-rose-800"
          }`}
          data-testid="llm-extraction-confidence-badge"
        >
          {t("badges.dates_confidence_label", "Dates")}: {llmConfidence === "high"
            ? t("badges.high", "élevée")
            : llmConfidence === "medium" ? t("badges.medium", "moyenne")
              : t("badges.low", "faible")}
        </span>
      )}
    </div>
  );
}

/**
 * Needs-Review banner shown when the document went into needs_review (no
 * trusted analysis available). Replaces ALL business data fields with a
 * clear call-to-action: re-run analysis with OCR.
 */
export function NeedsReviewBanner({ onReanalyze, isReanalyzing }) {
  const { t } = useTranslation("analysis");
  return (
    <div
      className="rounded-xl border border-amber-200 bg-amber-50 p-5 sm:p-6 mt-4"
      data-testid="needs-review-banner"
    >
      <div className="flex items-start gap-3">
        <ShieldAlert size={20} className="text-amber-700 shrink-0 mt-0.5" strokeWidth={2} />
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-amber-900">
            {t("needs_review.title", "Analyse non fiable — vérification requise")}
          </p>
          <p className="text-sm text-amber-800 mt-1">
            {t("needs_review.body",
              "L'extraction de texte (native + OCR) n'a pas pu produire un contenu exploitable. Aucune donnée métier (dates, montants, échéances) n'a été générée pour ce document. Cliquez sur 'Re-analyser' pour relancer l'extraction OCR.")}
          </p>
          <button
            type="button"
            onClick={onReanalyze}
            disabled={isReanalyzing}
            className="inline-flex items-center gap-1.5 mt-3 h-9 px-3 rounded-lg bg-amber-700 hover:bg-amber-800 text-white text-xs font-medium transition-colors disabled:opacity-50"
            data-testid="needs-review-reanalyze-btn"
          >
            {isReanalyzing
              ? t("needs_review.relaunching", "Relance en cours…")
              : t("needs_review.relaunch", "Re-analyser avec OCR")}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Inline citation chip — rendered next to a critical date to show the
 * source quote + page from the document.
 */
export function DateCitation({ citation }) {
  if (!citation?.quote) return null;
  const quote = citation.quote.length > 80 ? citation.quote.slice(0, 80) + "…" : citation.quote;
  const pageLabel = citation.page ? `· p.${citation.page}` : "";
  return (
    <span
      title={citation.quote}
      className="inline-flex items-center gap-1 text-[11px] text-zinc-500 mt-1 italic"
      data-testid="date-citation"
    >
      <Quote size={10} strokeWidth={1.75} />
      <span>"{quote}" {pageLabel}</span>
    </span>
  );
}
